import { Control, Controller, useFormState } from 'react-hook-form';
import { Box, useTheme, StyledDatePicker, Typography } from '../../../../../../base';
import { StyledOptionsContainer } from '../../JobQuestions.styles';
import { useTranslation } from 'react-i18next';
import {
    IJobQuestionsForm,
    JobQuestionObjectFields,
} from '../../../../../../common';
import moment, { Moment } from 'moment';

interface QuestionDatePickerProps {
    isDateRange: boolean;
    isDealbreaker?: boolean;
    fieldNamePrefix?: `questions.${number}`;
    control?: Control<IJobQuestionsForm>;
    index?: number;
    hasBeenSubmitted?: boolean;
}

export const QuestionDatePicker = ({
    isDateRange,
    isDealbreaker = false,
    fieldNamePrefix,
    control,
    index,
    hasBeenSubmitted = false,
}: QuestionDatePickerProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { errors } = useFormState({ control });

    const dateFromError = errors?.questions?.[index!]?.dateFrom;
    const dateToError = errors?.questions?.[index!]?.dateTo;

    const disabledInputSx = {
        '& .MuiInputBase-root': {
            borderRadius: '15px',
            backgroundColor: theme.palette.grey[100],
            cursor: 'not-allowed',
        },
        '& .MuiInputBase-input': {
            color: theme.palette.text.disabled,
            WebkitTextFillColor: theme.palette.text.disabled,
        },
    };

    const enabledInputSx = {
        '& .MuiInputBase-root': {
            borderRadius: '15px',
        },
    };

    if (!isDealbreaker || !fieldNamePrefix || !control) {
        return (
            <StyledOptionsContainer>
                <Box
                    sx={{
                        display: 'flex',
                        gap: theme.spacing(2),
                        flexWrap: 'wrap',
                        width: '100%',
                    }}
                >
                    {isDateRange ? (
                        <>
                            <Box sx={{ minWidth: '200px', maxWidth: '250px' }}>
                                <StyledDatePicker
                                    label={t('saveJob.fromDate')}
                                    value={null}
                                    onChange={() => {}}
                                    disabled
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            size: 'small',
                                            placeholder: t('saveJob.selectDate'),
                                            sx: disabledInputSx,
                                        },
                                    }}
                                    format="DD/MM/YYYY"
                                />
                            </Box>
                            <Box sx={{ minWidth: '200px', maxWidth: '250px' }}>
                                <StyledDatePicker
                                    label={t('saveJob.toDate')}
                                    value={null}
                                    onChange={() => {}}
                                    disabled
                                    slotProps={{
                                        textField: {
                                            variant: 'outlined',
                                            size: 'small',
                                            placeholder: t('saveJob.selectDate'),
                                            sx: disabledInputSx,
                                        },
                                    }}
                                    format="DD/MM/YYYY"
                                />
                            </Box>
                        </>
                    ) : (
                        <Box sx={{ minWidth: '200px', maxWidth: '250px' }}>
                            <StyledDatePicker
                                label={t('saveJob.selectDate')}
                                value={null}
                                onChange={() => {}}
                                disabled
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        size: 'small',
                                        placeholder: t('saveJob.selectDate'),
                                        sx: disabledInputSx,
                                    },
                                }}
                                format="DD/MM/YYYY"
                            />
                        </Box>
                    )}
                </Box>
            </StyledOptionsContainer>
        );
    }

    return (
        <StyledOptionsContainer>
            <Typography
                variant="body2"
                sx={{
                    color: theme.palette.text.primary,
                    marginBottom: theme.spacing(1),
                    fontWeight: 500,
                }}
            >
                {t('saveJob.dateRangeLabel')}
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    gap: theme.spacing(2),
                    flexWrap: 'wrap',
                    width: '100%',
                }}
            >
                <Box sx={{ minWidth: '200px', maxWidth: '250px' }}>
                    <Controller
                        name={
                            `${fieldNamePrefix}.${JobQuestionObjectFields.DATE_FROM}` as `questions.${number}.dateFrom`
                        }
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <StyledDatePicker
                                label={t('saveJob.fromDate')}
                                value={value ? moment(value) : null}
                                onChange={(date: Moment | null) =>
                                    onChange(date ? date.toDate() : null)
                                }
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        size: 'small',
                                        placeholder: t('saveJob.selectDate'),
                                        error:
                                            hasBeenSubmitted && !!dateFromError,
                                        helperText:
                                            hasBeenSubmitted && dateFromError
                                                ? dateFromError.message
                                                : '',
                                        sx: enabledInputSx,
                                    },
                                }}
                                format="DD/MM/YYYY"
                            />
                        )}
                    />
                </Box>
                <Box sx={{ minWidth: '200px', maxWidth: '250px' }}>
                    <Controller
                        name={
                            `${fieldNamePrefix}.${JobQuestionObjectFields.DATE_TO}` as `questions.${number}.dateTo`
                        }
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <StyledDatePicker
                                label={t('saveJob.toDate')}
                                value={value ? moment(value) : null}
                                onChange={(date: Moment | null) =>
                                    onChange(date ? date.toDate() : null)
                                }
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        size: 'small',
                                        placeholder: t('saveJob.selectDate'),
                                        error:
                                            hasBeenSubmitted && !!dateToError,
                                        helperText:
                                            hasBeenSubmitted && dateToError
                                                ? dateToError.message
                                                : '',
                                        sx: enabledInputSx,
                                    },
                                }}
                                format="DD/MM/YYYY"
                            />
                        )}
                    />
                </Box>
            </Box>
            <Typography
                variant="caption"
                sx={{
                    color: theme.palette.text.secondary,
                    marginTop: theme.spacing(0.5),
                    display: 'block',
                }}
            >
                {t('saveJob.dateRangeHelperText')}
            </Typography>
        </StyledOptionsContainer>
    );
};
